import { useNavigate, useParams } from "react-router-dom";
import { useLayoutEffect, useState, useEffect } from "react";
import FirstStep from "./FirstStep";
// import ImageSwiper from "components/ImageSwiper";
import SecondStep from "./SecondStep";
import ThirdStep from "./ThirdStep";
import FourthStep from "./FourthStep";
import Loader from "components/Loader";
// import uploadFiles from "api/media";
import { submitResponse } from "api/formResponse";
import { t } from "i18next";
import { useAppSelector } from "store/hooks";
import { getSettings } from "store/slices";
import axiosInstance from "utils/axios";
import useUser from "context/hooks/user";
import useMediaQuery from "hooks/useMediaQuery";
import { lifeChoiseDetailsDesktop } from "constantVariables";
import { useUserLogout } from "api/user";
import { validateFormData } from "utils/security";

export default function ResponseForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState();
  const [localeError, setLocalError] = useState("");
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({
    step: 0,
    data: {
      // first_name: "",
      // last_name: "",
      city: "",
      gender: null,
      date_of_birth: "",
      looking_for: "",
      tags: [],
      bars: [],
      questions: [],
      selectedQuestions: [],
    },
  });
  const [response_data, setResponseData] = useState({});
  const { dir, language, tenant: tenantDetail } = useAppSelector(getSettings);
  const { refetch } = useUser();
  const isDesktop = useMediaQuery(lifeChoiseDetailsDesktop);
  const { mutate: logout, isPending: loggingUserOut } = useUserLogout();
  const [ageWarning, setAgeWarning] = useState("");
  
  // Animation states
  const [showWelcome, setShowWelcome] = useState(false);
  const [showLine1, setShowLine1] = useState(false);
  const [showLine2, setShowLine2] = useState(false);
  const [showButtons, setShowButtons] = useState(false);

  // Animation effect - only run when step is 0 (welcome screen)
  useEffect(() => {
    let timeouts = [];
    
    if (formState.step === 0) {
      // Reset all states first
      setShowWelcome(false);
      setShowLine1(false);
      setShowLine2(false);
      setShowButtons(false);
      
      // Step 1: Welcome (3s) - fade in then fade out
      timeouts.push(setTimeout(() => setShowWelcome(true), 500));
      timeouts.push(setTimeout(() => setShowWelcome(false), 2500));

      // Step 2: Line 1 (3s) - fade in then fade out
      timeouts.push(setTimeout(() => setShowLine1(true), 3000));
      timeouts.push(setTimeout(() => setShowLine1(false), 5000));

      // Step 3: Line 2 (3s) - fade in and stay visible
      timeouts.push(setTimeout(() => setShowLine2(true), 5500));

      // Step 4: Buttons (permanent) - fade in and stay visible
      timeouts.push(setTimeout(() => setShowButtons(true), 8000));
    } else {
      // Reset animation states when not on welcome screen
      setShowWelcome(false);
      setShowLine1(false);
      setShowLine2(false);
      setShowButtons(false);
    }
    
    // Cleanup function to clear timeouts
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [formState.step]);

  // Clear error when form data changes
  useEffect(() => {
    if (localeError) {
      setLocalError("");
    }
  }, [formState.data]);

  const computeAgeWarning = (date_of_birth) => {
    if (!date_of_birth) {
      setAgeWarning("");
      return "";
    }
    const birthDate = new Date(date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();

    if (age < 16 || !date_of_birth) {
      setAgeWarning(t("registerUnderAge"));
      return;
    }
    if (age < 18 || !date_of_birth) {
      setAgeWarning(t("registerSubjectToGaurdian"));
      return;
    }
    setAgeWarning("");
  };

  useLayoutEffect(() => {
    setLoading(true);
    axiosInstance("users/form-response")
      .then((res) => res.data)
      .then((data) => {
        setLoading(false);
        if (data.statusCode === 200) {
          if (data.data.form_response) {
            navigate("/");
            return;
          }
          setData(data.data);
          setFormState((pre) => ({
            ...pre,
            data: {
              ...pre.data,
              city: data?.data?.city ?? pre.data.city,
              gender: data?.data?.gender ?? pre.data.gender,
              date_of_birth:
                data?.data?.date_of_birth
                  ? new Date(data?.data?.date_of_birth).getFullYear()
                  : pre.data.date_of_birth,
              // last_name: data?.data?.last_name ?? pre.data.last_name,
              // first_name: data?.data?.first_name ?? pre.data.first_name,
              bars:
                data?.data?.[
                  data?.data?.form_response ? "form_response" : "response_data"
                ]?.[language].bars?.map((bar) => ({
                  ...bar,
                  value: 5,
                })) ?? [],
              questions: data?.data?.[
                data?.data?.form_response ? "form_response" : "response_data"
              ]?.[language].questions?.map((question) => ({
                question,
                answer: "",
              })),
            },
          }));
          setResponseData(data?.data?.response_data[language] ?? {});
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setLocalError(err.message ?? "Something went wrong!");
      });
  }, [id]);

  async function handleSubmit() {
    setLocalError("");
    switch (formState.step) {
      case 1: {
        if (
          // formState.data.first_name &&
          // formState.data.last_name &&
          formState.data.city &&
          formState.data.gender &&
          formState.data.date_of_birth &&
          formState.data.looking_for
        ) {
          const birthDate = new Date(formState.data.date_of_birth);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          if (age < 16 || !formState.data.date_of_birth) {
            setLocalError(t("registerUnderAge"));
            return;
          }
          setFormState((pre) => ({
            ...pre,
            step: pre.step < 4 ? pre.step + 1 : pre.step,
          }));
          return;
        }
        setLocalError(t("registerFillAllFields"));
        break;
      }
      case 2: {
        if (formState.data.tags.length > 0) {
          setFormState((pre) => ({
            ...pre,
            step: pre.step < 4 ? pre.step + 1 : pre.step,
          }));
        } else {
          setLocalError(t("pleaseSelectOnetage"));
        }
        break;
      }
      case 3: {
        // Bars step - no validation needed, can proceed
        setFormState((pre) => ({
          ...pre,
          step: pre.step < 4 ? pre.step + 1 : pre.step,
        }));
        break;
      }
      case 4: {
        // Validate and sanitize form data before submission
        const validation = validateFormData(formState.data);
        
        if (!validation.isValid) {
          setLocalError(`Security validation failed: ${validation.errors.join(', ')}`);
          return;
        }
        
        // Check if user has selected at least one question
        if (!validation.sanitized.selectedQuestions || validation.sanitized.selectedQuestions.length === 0) {
          setLocalError(t("formDataQuestionSelectError"));
          return;
        }
        
        // Check if at least one selected question has an answer
        const answeredQuestions = validation.sanitized.selectedQuestions.filter(
          (questionIndex) => 
            validation.sanitized.questions[questionIndex] && 
            validation.sanitized.questions[questionIndex].answer.trim().length > 0
        );
        
        if (answeredQuestions.length === 0) {
          setLocalError(t("formDataQuestionAnswerError"));
          return;
        }
        
        if (answeredQuestions.length > 0) {
          setLoading(true);
          submitResponse({
            ...validation.sanitized,
            user_id: id,
            date_of_birth: new Date(
              `${new Date(validation.sanitized.date_of_birth.toString()).getFullYear()}-01-01`
            ),
          })
            .then((data) => {
              if (data.statusCode === 200) {
                refetch().then(() => {
                  setLoading(false);
                  navigate("/");
                });
              } else {
                setLoading(false);
                setLocalError(data.message);
              }
            })
            .catch((err) => {
              setLoading(false);
              setLocalError(err.message ?? t("someThingWentWrong"));
            });
        } else {
          setLocalError(t("formDataQuestionAnswerError"));
        }
        break;
      }
    }
  }

  return (
    <>
      <Loader open={loading || loggingUserOut} />
      {formState.step === 0 ? (
        <div dir={dir}>
          <div className="flex items-center justify-center px-6 mt-5">
            {/* <img
              src={require("../../assets/logo/logo.png")}
              alt="Nexme"
              className="w-[35vw] md:w-48 object-contain h-20"
            /> */}
            {tenantDetail &&
              (tenantDetail?.profile_image ? (
                <img
                  src={tenantDetail?.profile_image}
                  alt={tenantDetail?.institution_title}
                  className="w-[35vw] md:w-48 object-contain h-20"
                />
              ) : (
                <h2 className="font-bold text-2xl">{tenantDetail?.initials}</h2>
              ))}
          </div>
          <div className="h-[calc(100vh-100px)] flex flex-col items-center justify-center">
            {/* Animated Content - All in same position */}
            <div className="relative flex flex-col items-center justify-center min-h-[150px] w-full max-w-4xl">
              {/* Animated Welcome Message */}
              <div 
                className={`absolute transition-all duration-1000 ease-in-out ${
                  showWelcome 
                    ? 'opacity-100 transform translate-y-0' 
                    : 'opacity-0 transform translate-y-4'
                }`}
              >
                <h2 className="text-xl text-gray-700 font-medium text-center">
                  {t("signupWelcome")}&nbsp;
                  {data?.username ? "" : t("welcomeTo")}
                  <span className="capitalize font-bold text-blue-600">
                    {data?.username
                      ? data?.username
                      : tenantDetail.institution_title}
                  </span>
                  !
                </h2>
              </div>

              {/* Animated Example Line 1 */}
              <div 
                className={`absolute transition-all duration-1000 ease-in-out ${
                  showLine1 
                    ? 'opacity-100 transform translate-y-0' 
                    : 'opacity-0 transform translate-y-4'
                }`}
              >
                <p className="text-center text-xl text-gray-700 font-medium">
                  {t("signupAIGetToKnow")}
                </p>
              </div>

              {/* Animated Example Line 2 */}
              <div 
                className={`absolute transition-all duration-1000 ease-in-out ${
                  showLine2 
                    ? 'opacity-100 transform translate-y-0' 
                    : 'opacity-0 transform translate-y-4'
                }`}
              >
                <p className="text-center text-xl text-gray-700 font-medium">
                  {t("signupAIGetToKnowDescription")}
                </p>
              </div>
            </div>

            {/* Animated Start Button - Below the text area */}
            <div 
              className={`transition-all duration-1000 ease-in-out mt-8 ${
                showButtons 
                  ? 'opacity-100 transform translate-y-0' 
                  : 'opacity-0 transform translate-y-4'
              }`}
            >
              <button
                className="relative bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 text-white px-20 py-3 rounded-2xl font-bold text-xl shadow-2xl hover:shadow-blue-500/50 transition-all duration-500 hover:scale-105 w-full overflow-hidden group shimmer-button"
                onClick={() => {
                  setFormState((pre) => ({ ...pre, step: pre.step + 1 }));
                }}
              >
                {/* Shimmer overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent transform -skew-x-12 shimmer-sweep"></div>
                
                {/* Button content */}
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <span>{t("signupStart")}</span>
                  <svg 
                    className={`w-5 h-5 group-hover:translate-x-1 transition-transform duration-300 ${dir === 'rtl' ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>

            </div>
          </div>

          {/* Logout Button - Fixed at bottom of page */}
          <div 
            className={`transition-all duration-1000 ease-in-out fixed bottom-6 left-1/2 transform -translate-x-1/2 ${
              showButtons 
                ? 'opacity-100 transform -translate-x-1/2 translate-y-0' 
                : 'opacity-0 transform -translate-x-1/2 translate-y-4'
            }`}
          >
            <button
              className="text-gray-600 underline font-medium py-1 px-4 rounded-full focus:outline-none focus:shadow-outline text-sm disabled:opacity-50 hover:bg-gray-100 transition-all duration-300 hover:scale-105"
              onClick={logout}
            >
              {t("logOutMenuItem")}
            </button>
          </div>
        </div>
      ) : (
        <div className="min-h-screen w-screen flex flex-col bg-gradient-to-br from-blue-50 to-indigo-100" dir={dir}>
          {/* Header - Logo at top */}
          <div className="flex items-center justify-center w-full py-6">
            {tenantDetail?.profile_image ? (
              <a 
                href={tenantDetail?.website} 
                target="_blank" 
                rel="noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img
                  src={tenantDetail?.profile_image}
                  alt={tenantDetail?.institution_title}
                  className="w-32 md:w-48 object-contain h-16 md:h-20"
                />
              </a>
            ) : (
              <div className="w-32 md:w-48 h-16 md:h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-gray-400 text-sm">Logo</span>
              </div>
            )}
          </div>

          {/* Main Content - Centered */}
          <div className="flex-1 flex items-center justify-center px-4 py-4">
            <div className="w-full max-w-4xl mx-auto">

            {/* Form Content */}
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
              {/* Progress Indicators - Top of Card */}
              <div className="bg-gray-50 px-3 md:px-6 py-3 border-b border-gray-200">
                <div className="relative flex items-center justify-center">
                  {/* Back Button - Absolute positioned */}
                  {formState.step > 1 && (
                    <button
                      className={`absolute ${dir === "rtl" ? "right-0" : "left-0"} flex items-center gap-1 px-2 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-all duration-200 text-sm font-medium`}
                      onClick={() => {
                        setFormState((pre) => ({
                          ...pre,
                          step: pre.step >= 1 ? pre.step - 1 : pre.step,
                        }));
                      }}
                    >
                      <svg className={`w-4 h-4 ${dir === "rtl" ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      <span className="hidden sm:inline">{t("signupBack")}</span>
                    </button>
                  )}
                  
                  {/* Progress Indicator - Medium Wide Rounded Lines */}
                  <div className="flex items-center gap-2" role="progressbar" aria-label={`Step ${formState.step} of 4`}>
                    {[1, 2, 3, 4].map((step) => (
                      <div
                        key={step}
                        className={`h-2.5 rounded-full transition-all duration-500 ${
                          step < formState.step 
                            ? 'bg-blue-600 w-12' 
                            : step === formState.step
                            ? 'bg-blue-500 w-12 ring-2 ring-blue-200'
                            : 'bg-gray-300 w-8'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-4 lg:p-8" dir={dir}>
                {formState.step === 1 ? (
                  <FirstStep
                    formState={formState}
                    setFormState={setFormState}
                    data={data}
                    computeAgeWarning={computeAgeWarning}
                    ageWarning={ageWarning}
                  />
                ) : formState.step === 2 ? (
                  <SecondStep
                    formState={formState}
                    setFormState={setFormState}
                    tags={response_data.tags}
                  />
                ) : formState.step === 3 ? (
                  <ThirdStep
                    formState={formState}
                    setFormState={setFormState}
                    bars={response_data.bars}
                  />
                ) : (
                  formState.step === 4 && (
                    <FourthStep
                      formState={formState}
                      setFormState={setFormState}
                      questions={response_data.questions}
                      setLocalError={setLocalError}
                    />
                  )
                )}
              </div>

              {/* Navigation Footer - Full Width Button */}
              <div className="bg-gray-50 px-3 md:px-6 py-3 border-t border-gray-200">
                {/* Error Message - Above Button */}
                {localeError && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-600 text-center">{localeError}</p>
                  </div>
                )}
                
                <button
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-3 rounded-lg font-semibold text-sm shadow-md hover:shadow-lg disabled:shadow-none transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{t("signupLoading")}</span>
                    </div>
                  ) : (
                    formState.step === 4 ? t("signupFinish") : t("signupContinue")
                  )}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
